import React, {useEffect, useRef} from 'react';
import {Route, useHistory, useLocation} from 'react-router-dom';
import {ContentTemplate, Progress} from 'web-library';
import {useTranslation} from 'react-i18next';
import {AuthReturn} from '../logic/auth';

interface RouteProps {
  children: React.ReactNode;
  authHook: AuthReturn;
  exact?: boolean;
  auth: string;
  path: string;
  login: string;
  isMatchedBefore: boolean;
  isLoading: boolean;
}

function PrivateRoutePage({
                            authHook,
                            auth,
                            exact,
                            path,
                            login,
                            isMatchedBefore,
                            children,
                            isLoading,
                          }: RouteProps) {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const hasNavigatedToLogin = useRef(false);
  const loginPath = `/${auth}/${login}`;
  const shouldRedirectToLogin = !isLoading && !authHook.isUserLoggedIn;
  const loginRedirectMessage = authHook.isUserSessionInactive
      ? t('profile->>PrivateRoute.Session expiry! Please login again.')
      : undefined;

  useEffect(() => {
    if (!shouldRedirectToLogin) {
      hasNavigatedToLogin.current = false;
      return;
    }

    if (hasNavigatedToLogin.current || location.pathname === loginPath) {
      return;
    }

    hasNavigatedToLogin.current = true;
    history.replace({
      pathname: loginPath,
      state: {
        from: {
          pathname: location.pathname,
          search: location.search,
        },
        ...(loginRedirectMessage ? {message: loginRedirectMessage} : {}),
      },
    });
  }, [history, location.pathname, location.search, loginPath, loginRedirectMessage, shouldRedirectToLogin]);

  if (isLoading) {
    return (
        <ContentTemplate transparent>
          <Progress color="secondary"/>
        </ContentTemplate>
    );
  }

  return (
      <Route
          exact={exact}
          path={path}
          render={() => {
            if (shouldRedirectToLogin) {
              return (
                  <ContentTemplate transparent>
                    <Progress color="secondary"/>
                  </ContentTemplate>
              );
            }

            if (!isMatchedBefore) {
              return (
                  <ContentTemplate transparent>
                    <Progress color="secondary"/>
                  </ContentTemplate>
              );
            }

            return children;
          }}
      />
  );
}

export default PrivateRoutePage;
